const API_BASE = import.meta.env.VITE_API_URL || '/api'

/**
 * Resolve an image URL that may be relative to the backend API.
 * - Absolute URLs (http://, https://, //) are returned as-is.
 * - Data URLs are returned as-is.
 * - Relative paths starting with `/uploads/` are prefixed with the API origin.
 * - Other relative paths are prefixed with the API origin as well.
 * - Falsy values are returned as-is so callers can show fallbacks.
 */
export function resolveImageUrl(url) {
  if (!url) return url
  if (typeof url !== 'string') return url

  // Already absolute
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) {
    return url
  }

  // Prepend API origin for backend-served asset paths (/uploads/, /Speakers/, /Tutors/).
  // Other relative paths are treated as frontend public assets.
  const isBackendAssetPath =
    url.startsWith('/uploads/') || url === '/uploads' || url.startsWith('uploads/') ||
    url.startsWith('/Speakers/') || url.startsWith('Speakers/') ||
    url.startsWith('/Tutors/') || url.startsWith('Tutors/')
  if (isBackendAssetPath) {
    const baseOrigin = API_BASE.replace(/\/api\/?$/, '')
    const cleanPath = url.startsWith('/') ? url : `/${url}`
    return `${baseOrigin}${cleanPath}`
  }

  return url
}

export function resolveUploadUrl(url) {
  return resolveImageUrl(url)
}

// Speaker images stored in the public Speakers/ folder.
// Used as a fallback when the database has not yet stored a photo_url.
const SPEAKER_IMAGE_FILES = [
  'David Mase.jpeg',
  'Herbert.jpg',
  'Jjumba John.jpg',
  'Kulu Eric.jpg',
  'Ndiwalana Khasim.jpg',
  'Tonny.jpg',
]

function normalizeName(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/\b(mr|mrs|ms|dr|prof)\.?\b/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
}

function scoreSpeakerImage(name, filename) {
  const nameWords = normalizeName(name)
  const base = filename.replace(/\.[^.]+$/, '')
  const imgWords = normalizeName(base)
  if (!imgWords.length) return 0

  let matched = 0
  for (const iw of imgWords) {
    const wordMatch = nameWords.some(nw =>
      nw === iw || (iw.length >= 3 && nw.includes(iw)) || (nw.length >= 3 && iw.includes(nw))
    )
    if (!wordMatch) return 0
    matched += 1
  }
  return matched
}

export function getSpeakerObjectPosition(name, fit = 'card') {
  if (fit === 'hero') return '50% 12%'
  const words = normalizeName(name)
  const n = words.join(' ')
  if (n.includes('david') && n.includes('mase')) return '50% 32%'
  if (n.includes('herbert')) return '50% 10%'
  if (n.includes('jjumba')) return '50% 12%'
  if (n.includes('kulu') && n.includes('eric')) return '50% 14%'
  if (n.includes('ndiwalana') || n.includes('khasim')) return '50% 18%'
  if (n.includes('tonny')) return '50% 16%'
  return '50% 22%'
}

export function getSpeakerImageClasses(fit = 'card') {
  return fit === 'thumb'
    ? 'object-cover'
    : 'w-full h-full object-cover'
}

export function resolveSpeakerPhoto(speaker) {
  if (speaker?.photo_url) return resolveImageUrl(speaker.photo_url)
  if (speaker?.photo) return resolveImageUrl(speaker.photo)
  if (!speaker?.name) return null

  const candidates = SPEAKER_IMAGE_FILES
    .map(file => ({ file, score: scoreSpeakerImage(speaker.name, file) }))
    .filter(c => c.score > 0)
    .sort((a, b) => b.score - a.score)

  if (!candidates.length) return null
  if (candidates.length > 1 && candidates[0].score === candidates[1].score) return null

  return resolveImageUrl(`/Speakers/${candidates[0].file}`)
}
